import { Flex } from 'antd'
import { ProgressHeader } from 'pages/auth/registration/components/ProgressHeader'
import { useRef, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { GridContainer } from 'shared/ui'
import CodeInput, { CodeInputRef } from 'shared/ui/CodeInput/CodeInput'
import { useConfirmPhoneCodeMutation, useSendPhoneCodeQuery } from '../api'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { setActivity } from 'shared/config/store/reducers/appSlice'
import { useMessage } from 'shared/libs/hooks'
import { ApiError } from 'shared/model/types'
import { ROUTES } from 'shared/const/routes'
import { useLazyGetUserInfoQuery, useLazyGetUserQuery, useSaveGtagMutation } from 'entities/user';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { GTAG_ID } from 'shared/const/app'

export const ConfirmPhone = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const phone = params.get('phone');
  const codeRef = useRef<CodeInputRef>(null);
  const { formatMessage: t } = useIntl();
  const user = useSelector(getUserMeta);
  const email = params.get('email') || user.email;
  // Проверяем статус подтверждения email перед отправкой SMS
  const isEmailVerified = user.verified;
  const { isLoading: isLoadingSendPhone, refetch: refetchCodeSend } = useSendPhoneCodeQuery(undefined, {
    skip: !isEmailVerified, // Не отправляем SMS, если email не подтвержден
  });
  const [confirmPhoneCode, { isLoading: isLoadingConfirmPhone }] = useConfirmPhoneCodeMutation();
  const [fetchUserInfo] = useLazyGetUserInfoQuery();
  const [fetchUser] = useLazyGetUserQuery();
  const [saveGtag] = useSaveGtagMutation();
  
  const dispatch = useDispatch();
  const { showSuccess, showError } = useMessage();

  // Проверяем статус email при монтировании компонента
  useEffect(() => {
    if (!isEmailVerified && user.id !== 0) {
      // Если email не подтвержден, перенаправляем на страницу подтверждения email
      navigate({
        pathname: ROUTES.CONFIRM_EMAIL.path,
        search: `email=${email}&phone=${phone || user.phone}`
      });
    }
  }, [isEmailVerified, user.id, email, phone, user.phone, navigate]);

  const handleCodeConfirm = (code: string) => {
    // Проверяем статус email перед подтверждением телефона
    if (!isEmailVerified) {
      showError(t({ id: 'schemas.invalid' }));
      navigate({
        pathname: ROUTES.CONFIRM_EMAIL.path,
        search: `email=${email}&phone=${phone || user.phone}`
      });
      return;
    }

    dispatch(setActivity(true));
    confirmPhoneCode({ code })
      .unwrap()
      .then(async () => {
        gtag('get', GTAG_ID, 'client_id', (clientID) => {
          saveGtag({ clientId: clientID as string, eventName: 'web_phone_verification_completed', gtag: GTAG_ID });
          gtag('event', 'web_phone_verification_completed', {
            phone_verification: true
          });
        });
        codeRef.current?.success();
        await Promise.all([
          fetchUserInfo(),
          fetchUser(),
        ]);
        // Показ сообщения об успехе и навигация только при успешном подтверждении телефона
        showSuccess(t({ id: 'loginPage.welcome' }));
        navigate(ROUTES.DASHBOARD.path)
      })
      .catch((error: ApiError) => {
        console.log(error);
        codeRef.current?.clear();
        codeRef.current?.error(t({ id: 'schemas.invalid' }));
      })
      .finally(() => {
        dispatch(setActivity(false));
      })
  }

  const handleResend = () => {
    // Проверяем статус email перед повторной отправкой SMS
    if (!isEmailVerified) {
      showError(t({ id: 'schemas.invalid' }));
      navigate({
        pathname: ROUTES.CONFIRM_EMAIL.path,
        search: `email=${email}&phone=${phone || user.phone}`
      });
      return;
    }
    refetchCodeSend();
  }

  return (
    <GridContainer>
      <Flex vertical gap={40}>
        <ProgressHeader sectionIndex={5} last={6} title={t({ id: 'profile.settings.phone.confirm' })} onBack={() => navigate(ROUTES.LOGIN.path)} description={t({ id: 'profile.settings.phone.codeSent' }, { phone: phone })} />
        <Flex justify='center'>
          <CodeInput timeout={30} ref={codeRef} isPhone onResend={handleResend} onEnter={handleCodeConfirm} />
        </Flex>
      </Flex>
    </GridContainer>
  )
}